import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, ArrowRight, ArrowDown, X, Star, Zap, Trash2, ArrowUpCircle, RefreshCw, Layers, ShieldCheck, Dice5, Check, ChevronRight, Sparkles, Skull, AlertCircle, Filter, ArrowUpDown, Heart } from 'lucide-react';
import CurrencyIcon from './CurrencyIcon';
import { UserData } from '../hooks/useUser';
import { Prism, getPrismDescription, getPotentialDescription, calculatePrismBuffs, PrismBuffs, getBaseTraitValue } from '../utils/prismSystem';
import { cn } from '../lib/utils';
import Prism3D from './Prism3D';
import { audioEngine } from '../utils/audioEngine';
import { CloudIcon, ShellIcon, GoldRushIcon } from './ModeIcons';
import { useTranslation } from 'react-i18next';

interface PrismManagerProps {
  user: UserData;
  onClose: () => void;
  onEquip: (id: string | null, mode?: 'all' | 'pemdas' | 'shellcrush' | 'goldrush') => void;
  onEvolve: (ids: string[]) => Promise<Prism | null>;
  onReroll: (prismId: string, fodderIds: string[]) => Promise<{ current: Prism, old: Prism } | null>;
  onCommitReroll: (prismId: string, fodderIds: string[], kept: Prism) => Promise<void>;
  onAugment: (id: string) => Promise<{ current: Prism, old: Prism, isAutomatic: boolean } | null>;
  onCommitAugment: (id: string, kept: Prism) => Promise<void>;
  onShowAlert?: (title: string, message: string, type?: 'success' | 'error' | 'info') => void;
  onToggleLock: (id: string) => Promise<void>;
  tutorialStep?: number;
  onNextTutorialStep?: () => void;
  initialEquipMode?: 'pemdas' | 'shellcrush' | 'goldrush' | null;
}

type Mode = 'inventory' | 'evolve' | 'reroll' | 'augment';

const traitColorMap: Record<string, string> = {
  red: "#f43f5e", yellow: "#fbbf24", green: "#10b981", blue: "#38bdf8", violet: "#a855f7"
};

const potentialColorMap: Record<string, string> = {
  gc_multi: "#fbbf24",
  gc_bonus: "#fbbf24", // Legacy
  exp_bonus: "#10b981",
  reward_multi: "#f97316",
  hybrid_bonus: "#f97316", // Legacy
  reward_flat: "#6366f1",
  score_bonus: "#a855f7",
  fever_bonus: "#f43f5e",
  time_bonus: "#38bdf8"
};

export default function PrismManager({
  user, onClose, onEquip, onEvolve, onReroll, onCommitReroll, onAugment, onCommitAugment, onShowAlert, onToggleLock,
  tutorialStep, onNextTutorialStep, initialEquipMode
}: PrismManagerProps) {
  const { t } = useTranslation();
  const [activeEquipMode, setActiveEquipMode] = useState<'pemdas' | 'shellcrush' | 'goldrush'>(initialEquipMode || 'pemdas');
  const [mode, setMode] = useState<Mode>('inventory');
  const [selectedForEvolve, setSelectedForEvolve] = useState<string[]>([]);
  const [rerollTarget, setRerollTarget] = useState<string | null>(null);
  const [rerollFodders, setRerollFodders] = useState<string[]>([]);
  const [pendingReroll, setPendingReroll] = useState<{ current: Prism, old: Prism, fodderIds: string[] } | null>(null);
  const [pendingAugment, setPendingAugment] = useState<{ current: Prism, old: Prism } | null>(null);
  const [evolveSource, setEvolveSource] = useState<Prism | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sortBy, setSortBy] = useState<'stars' | 'color' | 'recent'>('stars');
  const [filterColor, setFilterColor] = useState<string>('all');
  const [filterStars, setFilterStars] = useState<number | null>(null);
  const [evolutionResult, setEvolutionResult] = useState<Prism | null>(null);
  const [showRerollConfirm, setShowRerollConfirm] = useState(false);
  const [bulkEvolveParams, setBulkEvolveParams] = useState<{ color: string; targetStars: number; sliderValue: number; steps: { totalCost: number; totalEvolved: number; totalConsumed: number; }[] } | null>(null);
  const [processingAction, setProcessingAction] = useState<'evolving' | 'rerolling' | null>(null);
  const [isFodderModalOpen, setIsFodderModalOpen] = useState(false);
  const [activeFodderTab, setActiveFodderTab] = useState<'stable' | 'unstable'>('stable');
  const [evolveViewMode, setEvolveViewMode] = useState<'evolvable' | 'all'>('evolvable');

  const [hoveredPrismId, setHoveredPrismId] = useState<string | null>(null);

  const isValidFodderSelection = () => {
    if (!rerollTarget) return false;
    const tgt = prisms.find(p => p.id === rerollTarget);
    if (!tgt) return false;
    const selected = prisms.filter(p => rerollFodders.includes(p.id));
    if (selected.length === 1) {
      return selected[0].color === tgt.color;
    }
    if (selected.length === 2) {
      return selected.every(f => f.color !== tgt.color);
    }
    return false;
  };

  const withProcessingOverlay = async (label: 'evolving' | 'rerolling', action: () => Promise<void>) => {
    setProcessingAction(label);
    await new Promise(res => setTimeout(res, 1500));
    setProcessingAction(null);
    await action();
  };

  const equippedIds = [
    user.equippedPrismId,
    user.equippedPrismIds?.pemdas,
    user.equippedPrismIds?.shellcrush,
    user.equippedPrismIds?.goldrush
  ].filter(Boolean) as string[];
  const prisms = user.prismInventory || [];
  const blackPrismCount = user.inventory?.items?.filter(i => i === 'black_prism').length || 0;

  // Auto-clear stale selections if prisms are consumed/deleted
  React.useEffect(() => {
    if (rerollTarget && !prisms.find(p => p.id === rerollTarget)) setRerollTarget(null);
    setRerollFodders(prev => prev.filter(id => prisms.some(p => p.id === id)));
  }, [prisms, rerollTarget]);

  // Tutorial automatic transitions
  React.useEffect(() => {
    if (tutorialStep === 39) {
      setFilterColor('all');
      setFilterStars(null);
    }
  }, [tutorialStep]);

  const getSortedPrisms = (prismList: Prism[]) => {
    let list = [...prismList];
    if (filterColor !== 'all') {
      list = list.filter(p => p.color === filterColor);
    }

    return list.sort((a, b) => {
      if (sortBy === 'stars') {
        const qualityA = (a.stars * 100) + (a.slot1.stars + (a.slot2?.stars || 0) + (a.slot3?.stars || 0));
        const qualityB = (b.stars * 100) + (b.slot1.stars + (b.slot2?.stars || 0) + (b.slot3?.stars || 0));
        return qualityB - qualityA;
      }
      if (sortBy === 'color') {
        return a.color.localeCompare(b.color);
      }
      return 0; // recent (default)
    });
  };

  const handleAugment = async (id: string) => {
    setIsProcessing(true);
    try {
      const result = await onAugment(id);
      if (result) {
        if (result.isAutomatic) {
          // Applied immediately
        } else {
          setPendingAugment({ current: result.current, old: result.old });
        }
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCommitAugment = async (kept: Prism) => {
    if (!pendingAugment) return;
    setIsProcessing(true);
    try {
      await onCommitAugment(pendingAugment.current.id, kept);
      setPendingAugment(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const calculateBulkEvolve = (color: string, targetStars: number) => {
    const evolvable = prisms.filter(p => p.color === color && p.stars === targetStars && !p.locked && !equippedIds.includes(p.id));
    let count = evolvable.length;

    const steps: { totalCost: number; totalEvolved: number; totalConsumed: number; }[] = [];
    let currentTotalCost = 0;
    let currentTotalEvolved = 0;
    let currentTotalConsumed = 0;

    while (count >= 3) {
      const cost = targetStars === 1 ? 2500 : (targetStars === 2 ? 10000 : 15000);
      currentTotalCost += cost;
      currentTotalEvolved += 1;
      currentTotalConsumed += 3;
      count -= 3;
      steps.push({
        totalCost: currentTotalCost,
        totalEvolved: currentTotalEvolved,
        totalConsumed: currentTotalConsumed
      });
    }

    if (steps.length === 0) {
      onShowAlert?.("NOT ENOUGH ANIMA", "Need 3 identical Anima to evolve.", "error");
      return;
    }
    // Default slider to the max amount the user can afford, or 1 if they can't afford any
    let initialSliderValue = steps.length;
    for (let i = steps.length - 1; i >= 0; i--) {
      if (user.coins >= steps[i].totalCost) {
        initialSliderValue = i + 1;
        break;
      }
    }
    if (user.coins < steps[0].totalCost) {
        initialSliderValue = 1;
    }
    setBulkEvolveParams({ color, targetStars, steps, sliderValue: initialSliderValue });
  };

  const executeBulkEvolve = async () => {
    if (!bulkEvolveParams || bulkEvolveParams.steps.length === 0) return;
    const { color, targetStars, sliderValue, steps } = bulkEvolveParams;
    const targetEvolutions = steps[sliderValue - 1].totalEvolved;
    setBulkEvolveParams(null);

    withProcessingOverlay('evolving', async () => {
      setIsProcessing(true);
      let totalEvolved = 0;
      let availablePrisms = [...prisms.filter(p => p.color === color && p.stars === targetStars && !p.locked && !equippedIds.includes(p.id))];
      let currentCoins = user.coins;
      
      try {
        while (totalEvolved < targetEvolutions) {
          const list = availablePrisms;
          if (list.length < 3) break;
          
          const cost = targetStars === 1 ? 2500 : (targetStars === 2 ? 10000 : 15000);
          if (currentCoins < cost) {
            onShowAlert?.("OUT OF COINS", `Stopped evolving: ${cost} coins required for ${targetStars}-Star evolution.`, "error");
            return;
          }
          
          const batchPrisms = list.slice(0, 3);
          const batch = batchPrisms.map(p => p.id);
          
          const result = await onEvolve(batch);
          if (result) {
            currentCoins -= cost;
            totalEvolved++;
            availablePrisms = availablePrisms.filter(p => !batch.includes(p.id));
          } else {
            break;
          }
        }
        audioEngine.playSound('success');
        onShowAlert?.("BULK EVOLVE COMPLETE", `Successfully evolved ${totalEvolved} Anima!`, "success");
      } catch (e: any) {
        onShowAlert?.("ERROR", e.message || "Bulk evolve failed", "error");
      } finally {
        setIsProcessing(false);
      }
    });
  };

  const renderPrismCard = (prism: Prism, options?: { isReady?: boolean, fodderCount?: number, isStatic?: boolean }) => {
    const activeModes: string[] = [];
    if (user.equippedPrismIds?.pemdas === prism.id) activeModes.push('CM');
    if (user.equippedPrismIds?.shellcrush === prism.id) activeModes.push('SC');
    if (user.equippedPrismIds?.goldrush === prism.id) activeModes.push('GR');
    // fallback for legacy
    if (!user.equippedPrismIds && prism.id === user.equippedPrismId) activeModes.push('ALL');

    const isEquipped = activeModes.length > 0;
    const isCorrupted = !!prism.blackStats;
    const isHovered = prism.id === hoveredPrismId;
    const isStatic = options?.isStatic !== undefined 
      ? options.isStatic 
      : (!isHovered && !isEquipped);

    const isDiamond = prism.stars === 3;
    const traitColor = traitColorMap[prism.color];
    const borderThickness = `${prism.stars}px`;
    const borderColor = prism.stars === 1 ? '#c27a4a' : prism.stars === 2 ? '#94a3b8' : '#fbbf24';

    return (
      <div 
        onMouseEnter={() => setHoveredPrismId(prism.id)}
        onMouseLeave={() => setHoveredPrismId(null)}
        className={cn(
          "aspect-square bg-slate-50/70 rounded-2xl flex flex-col items-center justify-center p-2 border border-slate-100 shadow-sm relative transition-all group",
          isEquipped && "ring-2 ring-[#0ea5e9] bg-white",
          isCorrupted && "ring-2 ring-slate-900 bg-slate-100"
        )}
        style={{
          boxShadow: `0 4px 10px -4px ${traitColorMap[prism.color]}40`
        }}
      >
        {/* Shiny Effect for Diamond Tier (Star 4) */}
        {isDiamond && (
          <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden rounded-[15px]">
            <div className="absolute inset-0 bg-gradient-to-tr from-amber-400/20 via-white/40 to-amber-400/20 opacity-30" />
          </div>
        )}

        {/* Lock Heart Toggle */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleLock(prism.id);
          }}
          className="absolute left-1.5 top-1.5 w-5 h-5 bg-white/90 backdrop-blur-sm rounded-full shadow-md flex items-center justify-center border border-slate-100 z-30 hover:scale-110 active:scale-95 transition-all"
        >
          <Heart className={cn("w-2.5 h-2.5 transition-all", prism.locked ? "text-rose-500 fill-rose-500" : "text-slate-300")} />
        </button>

        {isEquipped ? (
          <div className="absolute right-1 top-1 flex flex-col gap-0.5 z-30">
            {(activeModes.includes('CM') || activeModes.includes('ALL')) && (
              <div className="w-3.5 h-3.5 bg-sky-100/90 backdrop-blur-sm rounded-full shadow-md flex items-center justify-center p-0.5 border border-sky-200" title="Cloud Mach">
                <CloudIcon className="w-full h-full" />
              </div>
            )}
            {(activeModes.includes('SC') || activeModes.includes('ALL')) && (
              <div className="w-3.5 h-3.5 bg-amber-100/90 backdrop-blur-sm rounded-full shadow-md flex items-center justify-center p-0.5 border border-amber-200" title="Shell Crush">
                <ShellIcon className="w-full h-full" />
              </div>
            )}
            {(activeModes.includes('GR') || activeModes.includes('ALL')) && (
              <div className="w-3.5 h-3.5 bg-slate-100/90 backdrop-blur-sm rounded-full shadow-md flex items-center justify-center p-0.5 border border-slate-200" title="Gold Rush">
                <GoldRushIcon className="w-full h-full" />
              </div>
            )}
          </div>
        ) : (
          options?.isReady && (
            <div className="absolute right-1.5 top-1.5 bg-cyan-400 text-white text-[7px] font-black px-1.5 py-0.5 rounded-full shadow-lg z-30 animate-pulse border border-white">
              READY
            </div>
          )
        )}

        <div className="w-10 h-10 flex items-center justify-center scale-110 mt-1 relative z-20">
          <Prism3D color={prism.color} stars={prism.stars} isCorrupted={isCorrupted} isStatic={isStatic} potentials={[prism.slot1, prism.slot2, prism.slot3].filter(Boolean) as any} />
        </div>

        {/* Star Tier */}
        <div className="flex gap-0.5 mt-2 mb-1 h-2 relative z-20">
          {Array.from({ length: prism.stars }).map((_, i) => (
            <Star key={i} className={cn("w-2.5 h-2.5 fill-current", isCorrupted ? "text-violet-400" : "text-yellow-400")} />
          ))}
        </div>

        <div className="w-full flex items-center justify-center gap-0.5 mt-2 bg-slate-100/40 rounded-lg p-0.5 border border-slate-200/20 relative z-20">
          <div 
            className="w-[12px] h-[12px] rounded-md flex items-center justify-center shadow-sm shrink-0" 
            style={{ backgroundColor: traitColorMap[prism.color] }}
          >
            <Star className="w-2 h-2 fill-white text-white" />
          </div>
          <div className="w-[1px] h-3 bg-slate-300 mx-0.5 shrink-0" />
          {[prism.slot1, prism.slot2, prism.slot3].filter(Boolean).map((pot, idx) => (
            <div 
              key={idx} 
              className="w-[12px] h-[12px] rounded-md flex items-center justify-center shadow-sm shrink-0" 
              style={{ backgroundColor: potentialColorMap[pot!.type] }}
            />
          ))}
        </div>

        {isEquipped && (
          <div className="absolute -bottom-1.5 text-[7px] font-black px-1.5 py-0.5 rounded-full shadow-lg z-20 border bg-[#0ea5e9] text-white border-[#0369a1]">
            EQUIPPED
          </div>
        )}
      </div>
    );
  };
  return (
    <div className="flex flex-col h-full bg-[#f8fafc] text-slate-900 relative overflow-hidden">
      {/* Header with Currency */}
      <div className="bg-white border-b border-slate-100 px-6 py-4 flex flex-col gap-3 relative z-10 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              id="altar-close-button"
              onClick={() => {
                if (tutorialStep === 44 || tutorialStep === 48 || tutorialStep === 49) {
                  onNextTutorialStep?.();
                  onClose();
                } else {
                  onClose();
                }
              }}
              className="p-3 rounded-2xl bg-slate-50 border-2 border-slate-100 hover:bg-white transition-all active:translate-y-1 shadow-sm"
            >
              <ArrowLeft className="w-6 h-6 text-slate-400" />
            </button>
            <h2 className="text-3xl font-num font-black uppercase tracking-tight leading-none text-slate-900">{t('altar.title')}</h2>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 bg-yellow-400/10 px-3 py-1.5 rounded-2xl border border-yellow-400/20">
              <CurrencyIcon type="coin" size="sm" />
              <span className="font-num text-sm font-black text-yellow-600">{user.coins?.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1.5 bg-purple-500/10 px-3 py-1.5 rounded-2xl border border-purple-500/20">
              <CurrencyIcon type="gem" size="sm" />
              <span className="font-num text-sm font-black text-purple-600">{user.gems?.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex bg-slate-100 p-1 rounded-2xl">
          {(['inventory', 'evolve', 'reroll', 'augment'] as Mode[]).map(tabId => (
            <button
              key={tabId}
              id={`altar-tab-${tabId}`}
              onClick={() => {
                setMode(tabId);
                setPendingReroll(null);
                setRerollTarget(null);
                setRerollFodders([]);
                setPendingAugment(null);
                setEvolveSource(null);
                setSelectedForEvolve([]);
                setFilterColor('all');
              }}
              className={cn(
                "flex-1 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all",
                mode === tabId ? "bg-white text-cyan-600 shadow-sm" : "text-slate-400 hover:text-slate-600",
                tabId === 'augment' && (mode === tabId ? "text-violet-600" : "text-violet-400")
              )}
            >
              {tabId === 'inventory' && t('altar.tabs.vault')}
              {tabId === 'evolve' && t('altar.tabs.evolve')}
              {tabId === 'reroll' && t('altar.tabs.rerolls')}
              {tabId === 'augment' && (
                <div className="flex items-center justify-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  {t('altar.tabs.special')}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-4 scrollbar-none relative">
        <AnimatePresence mode="wait">
          {mode === 'inventory' && (
            <motion.div key="inv" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
              {/* Game Mode Equip Selector */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 px-1">
                  <Layers className="w-3.5 h-3.5 text-cyan-500" />
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('altar.vault.select_mode')}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 bg-slate-100 p-1.5 rounded-2xl">
                  {[
                    { id: 'pemdas', name: t('altar.mode_names.pemdas'), icon: CloudIcon, color: 'text-sky-500' },
                    { id: 'shellcrush', name: t('altar.mode_names.shellcrush'), icon: ShellIcon, color: 'text-orange-500' },
                    { id: 'goldrush', name: t('altar.mode_names.goldrush'), icon: GoldRushIcon, color: 'text-slate-600' }
                  ].map(m => {
                    const isSelected = activeEquipMode === m.id;
                    const equippedId = user.equippedPrismIds?.[m.id as 'pemdas' | 'shellcrush' | 'goldrush'];
                    return (
                      <button
                        key={m.id}
                        onClick={() => setActiveEquipMode(m.id as any)}
                        className={cn(
                          "relative py-3 rounded-xl flex flex-col items-center justify-center gap-1.5 transition-all shadow-sm border border-slate-100",
                          isSelected 
                            ? (m.id === 'pemdas' ? "bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] text-white border-b-4 border-[#0369a1]" :
                               m.id === 'shellcrush' ? "bg-gradient-to-br from-[#fef3c7] to-[#fde68a] text-slate-900 border-b-4 border-[#d97706]" :
                               "bg-gradient-to-br from-[#475569] to-[#1e293b] text-white border-b-4 border-slate-950")
                            : "bg-transparent hover:bg-slate-200 text-slate-400"
                        )}
                      >
                        <m.icon className={cn("w-6 h-6", isSelected ? "" : "opacity-40")} />
                        <span className={cn("text-[9px] font-black uppercase tracking-widest", isSelected ? "" : "text-slate-400")}>
                          {m.name}
                        </span>
                        {equippedId && (
                          <div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-cyan-400 border border-white shadow-sm" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Favorites Section - NOT affected by color/star filters */}
              {prisms.filter(p => p.locked).length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 px-1">
                    <Heart className="w-3.5 h-3.5 text-rose-500 fill-rose-500" />
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('altar.vault.favorites')}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[...prisms.filter(p => p.locked)].sort((a, b) => {
                      const qualityA = (a.stars * 100) + (a.slot1.stars + (a.slot2?.stars || 0) + (a.slot3?.stars || 0));
                      const qualityB = (b.stars * 100) + (b.slot1.stars + (b.slot2?.stars || 0) + (b.slot3?.stars || 0));
                      return qualityB - qualityA;
                    }).map(p => (
                      <div key={p.id} onClick={() => onEquip(p.id, activeEquipMode)} className="text-left group active:scale-95 transition-all cursor-pointer">
                        {renderPrismCard(p)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 2-Row Selector Filter */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 px-1">
                  <Filter className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('altar.vault.filters')}</span>
                </div>

                <div className="space-y-2">
                  {/* Row 1: Colors */}
                  <div className="flex bg-slate-100 p-1 rounded-2xl gap-1">
                    {['red', 'yellow', 'green', 'blue', 'violet'].map(c => {
                      const count = prisms.filter(p => p.color === c).length;
                      const isSelected = filterColor === c;
                      return (
                        <button
                          key={c}
                          onClick={() => setFilterColor(isSelected ? 'all' : c)}
                          className={cn(
                            "flex-1 h-8 rounded-xl flex flex-row items-center justify-center gap-1.5 transition-all relative border-2",
                            isSelected ? "bg-white border-slate-200 shadow-sm" : "border-transparent opacity-60"
                          )}
                        >
                          <div className="w-3 h-3 rounded-full shadow-sm shrink-0" style={{ backgroundColor: traitColorMap[c] }} />
                          <span className={cn("text-[10px] font-black transition-all", count > 0 ? "text-slate-900" : "text-slate-300")}>{count}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Row 2: Tiers */}
                  <div className="flex bg-slate-100 p-1 rounded-2xl gap-1">
                    {[3, 2, 1].map(s => {
                      const count = prisms.filter(p => p.stars === s && (filterColor === 'all' || p.color === filterColor)).length;
                      const isSelected = filterStars === s;
                      return (
                        <button
                          key={s}
                          onClick={() => setFilterStars(isSelected ? null : s)}
                          className={cn(
                            "flex-1 h-8 rounded-xl flex flex-row items-center justify-center gap-1.5 transition-all relative border-2",
                            isSelected ? "bg-white border-slate-200 shadow-sm" : "border-transparent opacity-60"
                          )}
                        >
                          <div className="flex gap-0.5">
                            {Array.from({ length: s }).map((_, i) => <Star key={i} className="w-2 h-2 text-yellow-400 fill-current" />)}
                          </div>
                          <span className={cn("text-[10px] font-black transition-all", count > 0 ? "text-slate-900" : "text-slate-300")}>{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Filtered Prism List */}
              <div className="space-y-2 pt-2 border-t border-slate-100">
                {(() => {
                  const filtered = getSortedPrisms(prisms).filter(p => {
                    const colorMatch = filterColor === 'all' || p.color === filterColor;
                    const starMatch = filterStars === null || p.stars === filterStars;
                    return colorMatch && starMatch;
                  });

                  if (filtered.length === 0) {
                    return (
                      <div className="py-12 text-center text-slate-400 font-black uppercase text-[10px] tracking-widest">
                        {t('altar.vault.no_prisms')}
                      </div>
                    );
                  }

                  return (
                    <div className="grid grid-cols-4 gap-2">
                      {filtered.map((p, index) => (
                        <div key={p.id} id={index === 0 ? "altar-prism-first" : undefined} onClick={() => onEquip(p.id, activeEquipMode)} className="text-left group active:scale-95 transition-all cursor-pointer">
                          {renderPrismCard(p)}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </motion.div>
          )}

          {mode === 'evolve' && (
            <motion.div key="evolve" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              {/* Evolve View Tabs */}
              <div className="flex bg-slate-100 p-1 rounded-2xl mb-4 w-full">
                <button 
                  onClick={() => setEvolveViewMode('evolvable')}
                  className={cn(
                    "flex-1 py-2 text-xs font-black uppercase tracking-widest rounded-xl transition-all",
                    evolveViewMode === 'evolvable' ? "bg-white text-slate-900 shadow-sm" : "text-slate-400 hover:text-slate-600"
                  )}
                >
                  {t('altar.evolve.view_evolvable')}
                </button>
                <button 
                  onClick={() => setEvolveViewMode('all')}
                  className={cn(
                    "flex-1 py-2 text-xs font-black uppercase tracking-widest rounded-xl transition-all",
                    evolveViewMode === 'all' ? "bg-white text-slate-900 shadow-sm" : "text-slate-400 hover:text-slate-600"
                  )}
                >
                  {t('altar.evolve.view_all')}
                </button>
              </div>

              {/* Color Filter Bar */}
              <div className="flex items-center justify-end gap-2 mb-4">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mr-2">{t('altar.evolve.quick_filter')}</span>
                <div className="flex items-center bg-slate-100 rounded-2xl p-1 gap-1">
                  {['all', 'red', 'yellow', 'green', 'blue', 'violet'].map(c => (
                    <button
                      key={c}
                      onClick={() => setFilterColor(c)}
                      className={cn(
                        "w-8 h-8 rounded-xl transition-all flex items-center justify-center",
                        filterColor === c ? "bg-white shadow-sm ring-1 ring-slate-200" : "opacity-40"
                      )}
                    >
                      {c === 'all' ? <Filter className="w-3.5 h-3.5 text-slate-400" /> : <div className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: traitColorMap[c] }} />}
                    </button>
                  ))}
                </div>
              </div>
              {(() => {
                const evolvablePrisms = prisms.filter(p => p.stars < 3 && !equippedIds.includes(p.id));
                const colors = ['red', 'yellow', 'green', 'blue', 'violet'];

                const readyByTier = { 1: 0, 2: 0 };
                colors.forEach(c => {
                  [1, 2].forEach(s => {
                    const pool = prisms.filter(f => !equippedIds.includes(f.id) && !f.locked && f.color === c && f.stars === s);
                    readyByTier[s as 1 | 2] += Math.floor(pool.length / 3);
                  });
                });
                
                const totalReady = readyByTier[1] + readyByTier[2];

                const evolvableByColor = colors.map(c => {
                  const tiers = [1, 2]
                    .map(s => {
                      const tierPrisms = getSortedPrisms(evolvablePrisms.filter(p => p.color === c && p.stars === s));
                      if (tierPrisms.length === 0) return null;
                      const fodderPool = prisms.filter(f => !equippedIds.includes(f.id) && !f.locked && f.color === c && f.stars === s);
                      const readySets = Math.floor(fodderPool.length / 3);
                      return { stars: s, prisms: tierPrisms, readySets, fodderCount: fodderPool.length };
                    })
                    .filter(Boolean) as { stars: number; prisms: any[]; readySets: number; fodderCount: number }[];
                  
                  const filteredTiers = evolveViewMode === 'evolvable' ? tiers.filter(t => t.readySets > 0) : tiers;
                  return { color: c, tiers: filteredTiers };
                }).filter(g => g.tiers.length > 0);

                if (evolvableByColor.length === 0) {
                  return <div className="py-20 text-center text-slate-400 font-num uppercase">{t('altar.evolve.no_evolvable')}</div>;
                }

                return (
                  <div className="space-y-6">
                    {/* Summary Banners */}
                    {totalReady > 0 && (
                      <div className="flex flex-col gap-2">
                        {[1, 2].map(tier => {
                          const count = tier === 1 ? readyByTier[1] : readyByTier[2];
                          if (count === 0) return null;
                          return (
                            <div key={tier} className="bg-cyan-50 border-2 border-cyan-100 rounded-3xl p-4 flex items-center justify-between">
                              <div>
                                <div className="text-[10px] font-black text-cyan-600 uppercase tracking-widest mb-0.5">{t('altar.evolve.ready_to_evolve')}</div>
                                <div className="text-2xl font-num text-cyan-700">{count} <span className="text-sm font-black">{count !== 1 ? t('altar.evolve.sets_plural') : t('altar.evolve.sets')}</span></div>
                              </div>
                              <div className="flex items-center gap-0.5">
                                {Array.from({ length: tier }).map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />)}
                                <span className="text-slate-400 font-black mx-1.5">→</span>
                                {Array.from({ length: tier + 1 }).map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {evolvableByColor.map(group => (
                      <div key={group.color} className="space-y-3">
                        <div className="flex items-center gap-2 px-1">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: traitColorMap[group.color] }} />
                          <h4 className="text-sm font-black text-slate-900 uppercase tracking-tight">{group.color}</h4>
                        </div>

                        {group.tiers.map(tier => (
                          <div key={tier.stars} className="bg-white rounded-3xl p-4 border-2 border-slate-100 space-y-3">
                            {/* Tier row: current stars → result stars + fodder count + evolve button */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {/* Current tier pill */}
                                <div className="flex items-center gap-1 bg-slate-50 rounded-2xl px-2.5 py-1.5">
                                  {Array.from({ length: tier.stars }).map((_, i) => <Star key={i} className="w-2.5 h-2.5 text-yellow-400 fill-current" />)}
                                  <span className="text-[9px] font-black text-slate-400 ml-1">{tier.fodderCount}</span>
                                </div>
                                <span className="text-slate-300 font-black">→</span>
                                {/* Result tier pill */}
                                <div className={cn(
                                  "flex items-center gap-1 rounded-2xl px-2.5 py-1.5 border",
                                  tier.readySets > 0 ? "bg-cyan-50 border-cyan-200" : "bg-slate-50 border-slate-100"
                                )}>
                                  {Array.from({ length: tier.stars + 1 }).map((_, i) => (
                                    <Star key={i} className={cn("w-2.5 h-2.5 fill-current", tier.readySets > 0 ? "text-yellow-400" : "text-slate-300")} />
                                  ))}
                                  {tier.readySets > 0 && (
                                    <span className="text-[9px] font-black text-cyan-600 ml-1">×{tier.readySets}</span>
                                  )}
                                  {tier.readySets === 0 && (
                                    <span className="text-[9px] font-black text-slate-300 ml-1">{t('altar.evolve.need_more', { count: 3 - (tier.fodderCount % 3 || 3) })}</span>
                                  )}
                                </div>
                              </div>

                              {tier.readySets > 0 && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); calculateBulkEvolve(group.color, tier.stars); }}
                                  disabled={isProcessing}
                                  className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500 text-white text-[10px] font-black uppercase rounded-full shadow-lg active:scale-95 transition-all hover:bg-cyan-600 disabled:opacity-50"
                                >
                                  <Zap className="w-3 h-3 fill-white" />
                                  ×{tier.readySets}
                                </button>
                              )}
                            </div>

                            {/* Prism cards */}
                            <div className="grid grid-cols-4 gap-2">
                              {tier.prisms.map((p, idx) => {
                                const fodderCount = prisms.filter(f =>
                                  f.id !== p.id &&
                                  !equippedIds.includes(f.id) &&
                                  !f.locked &&
                                  f.color === p.color &&
                                  f.stars === p.stars
                                ).length;
                                const isReady = fodderCount >= 2;
                                return (
                                  <div key={p.id}>
                                    <div
                                      id={idx === 0 ? "altar-evolve-first" : undefined}
                                      onClick={() => { setEvolveSource(p); setSelectedForEvolve([p.id]); }}
                                      className="text-left group active:scale-95 transition-all cursor-pointer"
                                    >
                                      {renderPrismCard(p, { isReady })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </motion.div>
          )}

          {mode === 'reroll' && (
            <motion.div key="reroll" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
              {!pendingReroll ? (
                <div className="space-y-6">
                  {/* Quick Color Filter Bar */}
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mr-2">{t('altar.reroll.quick_filter')}</span>
                    <div className="flex items-center bg-slate-100 rounded-2xl p-1 gap-1">
                      {['all', 'red', 'yellow', 'green', 'blue', 'violet'].map(c => (
                        <button
                          key={c}
                          onClick={() => setFilterColor(c)}
                          className={cn(
                            "w-8 h-8 rounded-xl transition-all flex items-center justify-center",
                            filterColor === c ? "bg-white shadow-sm ring-1 ring-slate-200" : "opacity-40"
                          )}
                        >
                          {c === 'all' ? <Filter className="w-3.5 h-3.5 text-slate-400" /> : <div className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: traitColorMap[c] }} />}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between px-2">
                      <span className="text-[10px] font-black text-slate-400 uppercase">{t('altar.reroll.target_label')}</span>
                      {rerollTarget && <button onClick={() => { setRerollTarget(null); setRerollFodders([]); }} className="text-[10px] text-rose-500 font-black uppercase">{t('altar.reroll.clear')}</button>}
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {prisms.filter(p => p.stars === 3 && (filterColor === 'all' || p.color === filterColor)).map((p, index) => {
                        const isTarget = rerollTarget === p.id;
                        return (
                          <div key={p.id} id={index === 0 ? "altar-reroll-target" : undefined} onClick={() => {
                            setRerollTarget(p.id);
                            if (rerollTarget !== p.id) {
                              setRerollFodders([]);
                              setActiveFodderTab('stable');
                            }
                            setIsFodderModalOpen(true);
                          }}
                            className={cn("text-left relative transition-all cursor-pointer", isTarget && "scale-105 ring-4 ring-amber-400 rounded-[24px]")}>
                            {renderPrismCard(p)}
                            {isTarget && <div className="absolute -top-2 -right-1 bg-amber-400 text-white text-[8px] px-2 py-0.5 rounded-full font-black shadow-lg z-10">{t('altar.vault.target')}</div>}
                            {p.locked && !isTarget && <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-900/60 backdrop-blur-sm text-white text-[8px] px-2 py-1 rounded-full font-black uppercase tracking-widest z-10">{t('altar.vault.locked')}</div>}
                          </div>
                        );
                      })}
                      {prisms.filter(p => p.stars === 3 && (filterColor === 'all' || p.color === filterColor)).length === 0 && (
                        <div className="col-span-4 py-8 text-center text-slate-400 text-[10px] font-black uppercase">
                          {filterColor === 'all' ? t('altar.reroll.no_3star') : t('altar.reroll.no_3star_color', { color: filterColor.toUpperCase() })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center w-full max-w-md mx-auto space-y-6">
                  <div className="text-center">
                    <h4 className="text-3xl font-num uppercase text-slate-900 tracking-tighter">{t('altar.reroll.successful_title')}</h4>
                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest mt-2">{t('altar.reroll.successful_sub')}</p>
                  </div>

                  {/* Vertical Stack with Arrow and Aggregated Stats on the Right */}
                  <div className="flex flex-col items-center gap-4 w-full px-2">
                    {/* OLD STATS ROW */}
                    <div className="flex items-center gap-6 w-full bg-white p-4 rounded-3xl border border-slate-100 shadow-sm relative">
                      <div className="w-[120px] shrink-0 text-left relative">
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-slate-400 text-white text-[8px] px-3 py-1 rounded-full z-10 whitespace-nowrap">{t('altar.reroll.old_stats')}</div>
                        {renderPrismCard(pendingReroll.old, { isStatic: true })}
                      </div>
                      
                      {/* Aggregated Stats */}
                      <div className="flex-1 min-w-0 flex flex-col justify-center space-y-1.5 border-l border-slate-100 pl-6">
                        {(() => {
                          const buffs = calculatePrismBuffs(pendingReroll.old);
                          const statList = [
                            buffs.coinMultiplier > 0 && { label: "Coin Bonus", val: `+${Math.round(buffs.coinMultiplier * 100)}%` },
                            buffs.expMultiplier > 0 && { label: "EXP Bonus", val: `+${Math.round(buffs.expMultiplier * 100)}%` },
                            buffs.scoreMultiplier > 0 && { label: "Score Bonus", val: `+${Math.round(buffs.scoreMultiplier * 100)}%` },
                            buffs.feverScoreMultiplier > 0 && { label: "Fever Score", val: `+${Math.round(buffs.feverScoreMultiplier * 100)}%` },
                            buffs.extraTime > 0 && { label: "Game Time", val: `+${buffs.extraTime}s` },
                            buffs.extraFever > 0 && { label: "Fever Time", val: `+${buffs.extraFever}s` },
                            buffs.flatCoins > 0 && { label: "Coins", val: `+${buffs.flatCoins}` },
                            buffs.flatExp > 0 && { label: "EXP", val: `+${buffs.flatExp}` },
                          ].filter(Boolean) as { label: string; val: string }[];

                          if (statList.length === 0) {
                            return <span className="text-[10px] font-bold text-slate-400 uppercase">{t('altar.reroll.no_active_stats')}</span>;
                          }

                          return statList.map((st, i) => (
                            <div key={i} className="flex justify-between items-center text-xs">
                              <span className="text-slate-500 font-bold uppercase text-[9px]">{st.label}</span>
                              <span className="text-slate-900 font-num font-black">{st.val}</span>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>

                    {/* DOWN ARROW */}
                    <div className="flex items-center justify-center bg-slate-100 rounded-full p-2.5 shadow-inner border border-slate-200">
                      <ArrowDown className="w-5 h-5 text-slate-400" />
                    </div>

                    {/* NEW STATS ROW */}
                    <div className="flex items-center gap-6 w-full bg-white p-4 rounded-3xl border border-slate-100 shadow-sm ring-2 ring-amber-400/20 relative">
                      <div className="w-[120px] shrink-0 text-left relative">
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-white text-[8px] px-3 py-1 rounded-full z-10 animate-pulse whitespace-nowrap">{t('altar.reroll.new_stats')}</div>
                        {renderPrismCard(pendingReroll.current, { isStatic: true })}
                      </div>
                      
                      {/* Aggregated Stats */}
                      <div className="flex-1 min-w-0 flex flex-col justify-center space-y-1.5 border-l border-slate-100 pl-6">
                        {(() => {
                          const buffs = calculatePrismBuffs(pendingReroll.current);
                          const statList = [
                            buffs.coinMultiplier > 0 && { label: "Coin Bonus", val: `+${Math.round(buffs.coinMultiplier * 100)}%` },
                            buffs.expMultiplier > 0 && { label: "EXP Bonus", val: `+${Math.round(buffs.expMultiplier * 100)}%` },
                            buffs.scoreMultiplier > 0 && { label: "Score Bonus", val: `+${Math.round(buffs.scoreMultiplier * 100)}%` },
                            buffs.feverScoreMultiplier > 0 && { label: "Fever Score", val: `+${Math.round(buffs.feverScoreMultiplier * 100)}%` },
                            buffs.extraTime > 0 && { label: "Game Time", val: `+${buffs.extraTime}s` },
                            buffs.extraFever > 0 && { label: "Fever Time", val: `+${buffs.extraFever}s` },
                            buffs.flatCoins > 0 && { label: "Coins", val: `+${buffs.flatCoins}` },
                            buffs.flatExp > 0 && { label: "EXP", val: `+${buffs.flatExp}` },
                          ].filter(Boolean) as { label: string; val: string }[];

                          if (statList.length === 0) {
                            return <span className="text-[10px] font-bold text-slate-400 uppercase">{t('altar.reroll.no_active_stats')}</span>;
                          }

                          return statList.map((st, i) => (
                            <div key={i} className="flex justify-between items-center text-xs">
                              <span className="text-slate-500 font-bold uppercase text-[9px]">{st.label}</span>
                              <span className="text-amber-600 font-num font-black">{st.val}</span>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Bottom keep/change buttons */}
                  <div className="grid grid-cols-2 gap-4 w-full px-2 pt-4">
                    <button
                      onClick={async () => {
                        await onCommitReroll(pendingReroll.old.id, pendingReroll.fodderIds, pendingReroll.old);
                        setPendingReroll(null);
                        setRerollTarget(null);
                        setRerollFodders([]);
                      }}
                      className="py-4 rounded-2xl font-num text-lg uppercase text-slate-600 bg-slate-100 hover:bg-slate-200 transition-all active:scale-95 border-2 border-slate-200/50"
                    >
                      {t('altar.reroll.keep')}
                    </button>
                    <button
                      onClick={async () => {
                        await onCommitReroll(pendingReroll.current.id, pendingReroll.fodderIds, pendingReroll.current);
                        setPendingReroll(null);
                        setRerollTarget(null);
                        setRerollFodders([]);
                      }}
                      className="py-4 rounded-2xl font-num text-lg uppercase text-white bg-slate-900 shadow-lg hover:bg-slate-800 transition-all active:scale-95"
                    >
                      {t('altar.reroll.change')}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {mode === 'augment' && (
            <motion.div key="special" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="py-32 flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-slate-50 rounded-[32px] flex items-center justify-center mb-6">
                <Sparkles className="w-10 h-10 text-slate-300" />
              </div>
              <h4 className="text-2xl font-num uppercase text-slate-400 tracking-tighter">{t('altar.special.title')}</h4>
              <div className="mt-2 px-4 py-1 bg-slate-100 rounded-full">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('altar.special.coming_soon')}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Fodder Selection Pop-up Modal */}
      <AnimatePresence>
        {isFodderModalOpen && rerollTarget && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-[1200] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" onClick={() => { setIsFodderModalOpen(false); setRerollTarget(null); setRerollFodders([]); }} />
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-[40px] w-full max-w-lg h-[85%] max-h-[580px] flex flex-col relative z-10 shadow-2xl overflow-hidden border-2 border-slate-100"
            >
              {/* Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-2xl"><RefreshCw className="w-5 h-5 text-amber-600" /></div>
                  <h4 className="text-xl font-num text-slate-900 uppercase font-black tracking-tight leading-none">{t('altar.reroll.fodder_title')}</h4>
                </div>
                <button
                  onClick={() => { setIsFodderModalOpen(false); setRerollTarget(null); setRerollFodders([]); }}
                  className="p-3 bg-slate-50 hover:bg-slate-100 rounded-2xl transition-all active:scale-95"
                >
                  <X className="w-4 h-4 text-slate-500" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4 scrollbar-none">
                {/* Tabs */}
                <div className="flex bg-slate-100 p-1 rounded-2xl shrink-0">
                  <button
                    onClick={() => { setActiveFodderTab('stable'); setRerollFodders([]); }}
                    className={cn(
                      "flex-1 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all",
                      activeFodderTab === 'stable' ? "bg-white text-emerald-600 shadow-sm" : "text-slate-400 hover:text-slate-600"
                    )}
                  >
                    {t('altar.reroll.tab_stable')}
                  </button>
                  <button
                    onClick={() => { setActiveFodderTab('unstable'); setRerollFodders([]); }}
                    className={cn(
                      "flex-1 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all",
                      activeFodderTab === 'unstable' ? "bg-white text-amber-600 shadow-sm" : "text-slate-400 hover:text-slate-600"
                    )}
                  >
                    {t('altar.reroll.tab_unstable')}
                  </button>
                </div>

                {/* Info Text */}
                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">
                  {activeFodderTab === 'stable' ? (
                    <span className="text-emerald-600">{t('altar.reroll.stable_req')}</span>
                  ) : (
                    <span className="text-amber-600">{t('altar.reroll.unstable_req')}</span>
                  )}
                </div>

                {/* Fodder List */}
                {(() => {
                  const target = prisms.find(p => p.id === rerollTarget)!;
                  const validFodder = prisms.filter(p => p.id !== target.id && p.stars === 3 && !p.locked && !equippedIds.includes(p.id));

                  const displayFodder = activeFodderTab === 'stable'
                    ? validFodder.filter(p => p.color === target.color)
                    : validFodder.filter(p => p.color !== target.color);

                  if (displayFodder.length === 0) {
                    return (
                      <div className="py-12 text-center text-slate-400 text-[10px] font-black uppercase">
                        {t('altar.reroll.no_fodder')}
                      </div>
                    );
                  }

                  return (
                    <div className="grid grid-cols-4 gap-2">
                      {displayFodder.map(p => {
                        const isFodder = rerollFodders.includes(p.id);
                        return (
                          <div
                            key={p.id}
                            onClick={() => {
                              if (activeFodderTab === 'stable') {
                                  if (isFodder) setRerollFodders([]);
                                  else setRerollFodders([p.id]);
                              } else {
                                if (isFodder) {
                                  setRerollFodders(prev => prev.filter(id => id !== p.id));
                                } else {
                                  if (rerollFodders.length < 2) {
                                    setRerollFodders(prev => [...prev, p.id]);
                                  }
                                }
                              }
                            }}
                            className={cn(
                              "text-left relative transition-all cursor-pointer",
                              (rerollFodders.length > 0 && !isFodder) && (
                                ((activeFodderTab === 'stable' || rerollFodders.length >= 2) ? "opacity-20 grayscale scale-90" : "")
                              ),
                              isFodder && cn(
                                "scale-105 ring-4 rounded-[24px]",
                                activeFodderTab === 'stable' ? "ring-emerald-400" : "ring-amber-400"
                              )
                            )}
                          >
                            {renderPrismCard(p)}
                            {isFodder && (
                              <div className={cn(
                                "absolute -top-2 -right-1 text-white text-[8px] px-2 py-0.5 rounded-full font-black shadow-lg z-10",
                                activeFodderTab === 'stable' ? "bg-emerald-400" : "bg-amber-400"
                              )}>
                                {t('altar.vault.fodder')}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Footer with Reroll Action */}
              <div className="p-6 border-t border-slate-100 bg-slate-50 shrink-0">
                <button
                  disabled={!isValidFodderSelection()}
                  onClick={() => {
                    setShowRerollConfirm(true);
                  }}
                  className={cn(
                    "w-full py-4 rounded-2xl font-num text-lg uppercase toy-3d-button border-none transition-all",
                    isValidFodderSelection()
                      ? "bg-amber-400 text-slate-900 shadow-md hover:bg-amber-500"
                      : "bg-slate-200 text-slate-400"
                  )}
                >
                  {isValidFodderSelection() ? t('altar.reroll.reroll_now') : (
                    activeFodderTab === 'stable' ? t('altar.reroll.select_same') : (
                      2 - rerollFodders.length === 1 
                        ? t('altar.reroll.select_more', { count: 1 })
                        : t('altar.reroll.select_more_plural', { count: 2 - rerollFodders.length })
                    )
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Evolution Pop-up Overlay (Moved outside scroll area) */}
      <AnimatePresence>
        {evolveSource && (
          <motion.div initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.1 }} className="absolute inset-0 z-[1000] bg-slate-900 flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-white/5">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-500/20 rounded-lg"><ArrowUpCircle className="w-5 h-5 text-cyan-400" /></div>
                <h4 className="text-xl font-num text-white uppercase italic tracking-tighter">{t('altar.tabs.evolve')}</h4>
              </div>
              <button onClick={() => {
                setEvolveSource(null);
                setSelectedForEvolve([]);
                setEvolutionResult(null);
              }} className="p-2.5 bg-white/5 hover:bg-white/10 rounded-xl transition-all"><X className="w-5 h-5 text-white" /></button>
            </div>
            <div className="flex-1 overflow-hidden px-6 pt-4 pb-32">
              {evolutionResult ? (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center py-4">
                  <div className="text-center mb-6">
                    <h4 className="text-3xl font-num text-white uppercase italic tracking-tighter leading-none">{t('altar.evolve.ascended_title')}</h4>
                    <p className="text-[10px] font-black text-cyan-400/60 uppercase tracking-[0.2em] mt-1">{t('altar.evolve.ascended_sub')}</p>
                  </div>

                  <div className="w-48 h-48 aspect-square bg-white rounded-[40px] shadow-xl border-2 border-slate-100 flex flex-col items-center justify-center p-4 mb-6 relative group">
                    <div className="w-24 h-24 mb-2">
                      <Prism3D color={evolutionResult.color} stars={evolutionResult.stars} isCorrupted={!!evolutionResult.blackStats} potentials={[evolutionResult.slot1, evolutionResult.slot2, evolutionResult.slot3].filter(Boolean) as any} />
                    </div>

                    <div className="flex gap-1 mb-3">
                      {Array.from({ length: evolutionResult.stars }).map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>

                    {/* Mini Stats Bar in Square */}
                    <div className="w-full bg-slate-50/80 rounded-2xl p-1.5 flex items-center justify-center gap-1.5 border border-slate-100 shadow-inner">
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center shadow-sm" style={{ backgroundColor: traitColorMap[evolutionResult.color] }}>
                        <Star className="w-3.5 h-3.5 fill-white text-white" />
                      </div>
                      <div className="w-[1px] h-4 bg-slate-200" />
                      {[evolutionResult.slot1, evolutionResult.slot2, evolutionResult.slot3].filter(Boolean).map((pot, i) => (
                        <div key={i} className="w-6 h-6 rounded-lg flex items-center justify-center shadow-sm" style={{ backgroundColor: potentialColorMap[pot.type] }} />
                      ))}
                    </div>
                  </div>
                  <div className="w-full bg-white/5 rounded-[32px] p-4 border border-white/10">
                    <div className="space-y-2">
                      {/* Trait Stat */}
                      <div className="flex items-center gap-4 bg-white/5 p-2 rounded-2xl border border-white/5">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg shrink-0" style={{ backgroundColor: traitColorMap[evolutionResult.color] }}>
                          <Star className="w-5 h-5 fill-white text-white" />
                        </div>
                        <div className="flex-1 flex justify-between items-center gap-2">
                          {(() => {
                            const desc = getPrismDescription(evolutionResult);
                            const [label, value] = desc.split(' +');
                            return (
                              <>
                                <div className="text-[12px] font-black text-white/60 uppercase">{label}</div>
                                <div className="text-[14px] font-black text-white">+{value}</div>
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Potentials 1-3 */}
                      {[evolutionResult.slot1, evolutionResult.slot2, evolutionResult.slot3].filter(Boolean).map((pot, i) => (
                        <div key={i} className="flex items-center gap-4 bg-white/5 p-2 rounded-2xl border border-white/5">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg shrink-0" style={{ backgroundColor: potentialColorMap[pot.type] }} />
                          <div className="flex-1 flex justify-between items-center gap-2">
                            {(() => {
                              const desc = getPotentialDescription(pot);
                              const parts = desc.split(' +');
                              if (parts.length === 1) {
                                // Handle cases where there might not be a space before +
                                const splitAlt = desc.split('+');
                                return (
                                  <>
                                    <div className="text-[12px] font-black text-white/60 uppercase">{splitAlt[0]}</div>
                                    <div className="text-[14px] font-black text-white">+{splitAlt[1]}</div>
                                  </>
                                );
                              }
                              return (
                                <>
                                  <div className="text-[12px] font-black text-white/60 uppercase">{parts[0]}</div>
                                  <div className="text-[14px] font-black text-white">+{parts[1]}</div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ) : (
                <>
                  <div className="flex flex-col items-center mb-12 relative">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-cyan-500/10 blur-[60px] rounded-full" />
                    <div className="w-40 h-40 mb-6 relative z-10"><Prism3D color={evolveSource.color} stars={evolveSource.stars} isCorrupted={!!evolveSource.blackStats} potentials={[evolveSource.slot1, evolveSource.slot2, evolveSource.slot3].filter(Boolean) as any} /></div>
                    <div className="text-center relative z-10">
                      <div className="flex gap-1 justify-center mb-2">
                        {Array.from({ length: evolveSource.stars }).map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-cyan-400 text-cyan-400" />
                        ))}
                      </div>
                      <h5 className="text-2xl font-num text-white uppercase tracking-tight">{evolveSource.color} Core</h5>
                      <p className="text-[10px] text-cyan-400/60 font-black uppercase tracking-[0.2em] mt-1">{t('altar.evolve.ready_for_transcendence')}</p>
                    </div>
                  </div>
                  {selectedForEvolve.length === 3 ? (() => {
                    const nextStars = evolveSource.stars + 1;
                    const originalVal = getBaseTraitValue(evolveSource.color, evolveSource.stars);
                    const upgradedVal = getBaseTraitValue(evolveSource.color, nextStars);

                    const formatTrait = (val: number) => {
                      if (evolveSource.color === 'red' || evolveSource.color === 'yellow' || evolveSource.color === 'green' || evolveSource.color === 'violet') {
                        return `+${Math.round((val - 1) * 100)}%`;
                      }
                      return `+${val}s`;
                    };

                    const traitName = evolveSource.color === 'red' ? 'Fever Score' :
                                      evolveSource.color === 'yellow' ? 'Coin Bonus' :
                                      evolveSource.color === 'green' ? 'EXP Bonus' :
                                      evolveSource.color === 'blue' ? 'Game Time' : 'Score Bonus';

                    return (
                      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-5 bg-white/5 border border-cyan-500/20 rounded-3xl space-y-4 my-2 relative overflow-hidden">
                        <div className="text-center font-num text-[10px] font-black uppercase tracking-widest text-cyan-400">{t('altar.evolve.projected')}</div>
                        
                        <div className="grid grid-cols-3 items-center text-center">
                          <div>
                            <div className="flex gap-0.5 justify-center mb-1">
                              {Array.from({ length: evolveSource.stars }).map((_, i) => (
                                <Star key={i} className="w-3.5 h-3.5 fill-cyan-400 text-cyan-400" />
                              ))}
                            </div>
                            <span className="text-[10px] text-slate-400 font-black uppercase tracking-wider">{t('altar.evolve.current')}</span>
                          </div>
                          <div className="flex flex-col items-center">
                            <ArrowRight className="w-5 h-5 text-cyan-400 animate-pulse" />
                            <span className="text-[8px] font-black text-cyan-400/60 uppercase mt-0.5">EVOLVE</span>
                          </div>
                          <div>
                            <div className="flex gap-0.5 justify-center mb-1">
                              {Array.from({ length: nextStars }).map((_, i) => (
                                <Star key={i} className="w-3.5 h-3.5 fill-emerald-400 text-emerald-400" />
                              ))}
                            </div>
                            <span className="text-[10px] text-emerald-400 font-black uppercase tracking-wider">{t('altar.evolve.evolved')}</span>
                          </div>
                        </div>

                        <div className="border-t border-white/5 pt-3 space-y-2.5">
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-slate-400 font-black uppercase tracking-wider">{traitName}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-300 font-num">{formatTrait(originalVal)}</span>
                              <ArrowRight className="w-3 h-3 text-slate-600" />
                              <span className="text-emerald-400 font-num font-black">{formatTrait(upgradedVal)}</span>
                            </div>
                          </div>

                          <div className="flex justify-between items-center text-xs">
                            <span className="text-slate-400 font-black uppercase tracking-wider">{t('altar.evolve.sub_stat_slots')}</span>
                            <div className="flex items-center gap-1.5 font-num text-[10px] font-black uppercase">
                              <span className="text-slate-400">{evolveSource.stars === 1 ? t('altar.evolve.slots_1star') : evolveSource.stars === 2 ? t('altar.evolve.slots_2star') : t('altar.evolve.slots_3star')}</span>
                              <ArrowRight className="w-3 h-3 text-slate-600" />
                              <span className="text-emerald-400">{nextStars === 2 ? t('altar.evolve.slots_2star') : t('altar.evolve.slots_3star')}</span>
                            </div>
                          </div>
                        </div>

                        <div className="text-center pt-1">
                          <button onClick={() => setSelectedForEvolve([evolveSource.id])} className="text-[10px] font-black text-slate-400 hover:text-white uppercase underline transition-colors">
                            {t('altar.evolve.change_fodder')}
                          </button>
                        </div>
                      </motion.div>
                    );
                  })() : (
                    <div className="space-y-4">
                      <div className="flex justify-between items-end px-2">
                        <div>
                          <span className="text-[10px] font-black text-white/40 uppercase tracking-widest block mb-1">{t('altar.evolve.eligible_fodder')}</span>
                          <span className="text-sm font-num font-black text-white">{selectedForEvolve.length - 1}</span>
                        </div>
                        <span className="text-[9px] font-black text-cyan-400 uppercase bg-cyan-400/10 px-3 py-1 rounded-full">{t('altar.evolve.same_color_rank')}</span>
                      </div>
                      <div id="altar-fodder-container" className="grid grid-cols-4 gap-2">
                        {prisms.filter(p => p.id !== evolveSource.id && p.color === evolveSource.color && p.stars === evolveSource.stars).map(p => {
                          const isSelected = selectedForEvolve.includes(p.id);
                          return (
                            <button key={p.id} disabled={(!isSelected && selectedForEvolve.length >= 3) || p.locked} onClick={() => {
                              if (isSelected) setSelectedForEvolve(prev => prev.filter(id => id !== p.id));
                              else setSelectedForEvolve(prev => [...prev, p.id]);
                            }} className={cn("text-left relative transition-all active:scale-95", isSelected ? "scale-105" : (p.locked ? "opacity-20 grayscale" : "opacity-40 grayscale-[0.5]"))}>
                              {renderPrismCard(p)}
                              {isSelected && (
                                <div className="absolute inset-0 bg-cyan-500/20 rounded-[24px] border-4 border-cyan-400 flex items-center justify-center">
                                  <div className="bg-cyan-400 text-white w-6 h-6 rounded-full flex items-center justify-center shadow-lg"><Check className="w-4 h-4" /></div>
                                </div>
                              )}
                              {p.locked && !isSelected && <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-900/60 backdrop-blur-sm text-white text-[8px] px-2 py-1 rounded-full font-black uppercase tracking-widest z-10">{t('altar.vault.locked')}</div>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="p-6 bg-slate-950 border-t border-white/5">
              {evolutionResult ? (
                <button 
                  id="altar-collect-btn"
                  onClick={() => {
                    setEvolveSource(null);
                    setSelectedForEvolve([]);
                    setEvolutionResult(null);
                    if (tutorialStep === 45) {
                      onNextTutorialStep?.();
                    }
                  }}
                  className="w-full py-5 rounded-2xl font-num text-xl uppercase bg-cyan-400 text-white toy-3d-button border-none shadow-[0_0_30px_rgba(34,211,238,0.3)]"
                >
                  {t('altar.evolve.collect')}
                </button>
              ) : (
                <button onClick={async () => {
                  console.log("Pop-up Evolve button clicked. IDs:", selectedForEvolve);
                  if (selectedForEvolve.length !== 3) return;
                  const firstPrism = prisms.find(p => p.id === selectedForEvolve[0]);
                  const baseStars = firstPrism ? firstPrism.stars : 1;
                  const cost = baseStars === 1 ? 2500 : (baseStars === 2 ? 10000 : 15000);
                  if (user.coins < cost) {
                    audioEngine.playNotEnough();
                    onShowAlert?.("NOT ENOUGH COINS", `Evolution costs ${cost} coins.`, "error");
                    return;
                  }
                  setIsProcessing(true);
                  try {
                    const result = await onEvolve(selectedForEvolve);
                    console.log("Evolution result:", result);
                    if (result) {
                      setEvolutionResult(result);
                      if (tutorialStep === 44) {
                        setTimeout(() => {
                          onNextTutorialStep?.();
                        }, 1500);
                      }
                    }
                  } catch (err: any) {
                    console.error("Evolution error:", err);
                    if (onShowAlert) onShowAlert(t('altar.alerts.error'), err.message || t('altar.alerts.error'), "error");
                  } finally {
                    setIsProcessing(false);
                  }
                }}
                  id="altar-evolve-btn"
                  disabled={selectedForEvolve.length !== 3 || isProcessing || (prisms.find(p => p.id === selectedForEvolve[0])?.stars ?? 1) >= 3}
                  className={cn("w-full py-5 rounded-2xl font-num text-xl uppercase toy-3d-button border-none transition-all",
                    selectedForEvolve.length === 3 ? "bg-cyan-400 text-white shadow-[0_0_30px_rgba(34,211,238,0.3)]" : "bg-white/5 text-white/20")}>
                  {isProcessing ? t('altar.evolve.transcending') : (selectedForEvolve.length === 3 ? (() => {
                    const firstPrism = prisms.find(p => p.id === selectedForEvolve[0]);
                    const baseStars = firstPrism ? firstPrism.stars : 1;
                    if (baseStars >= 3) return t('altar.evolve.max_tier');
                    const cost = baseStars === 1 ? 2500 : (baseStars === 2 ? 10000 : 15000);
                    return t('altar.evolve.evolve_now', { cost });
                  })() : t('altar.evolve.select_more', { count: 3 - selectedForEvolve.length }))}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
 
      {/* Action / Buff Footer */}
      <div className="p-6 pb-10 bg-slate-50 border-t border-slate-100 mt-auto" style={{ paddingBottom: 'max(2.5rem, env(safe-area-inset-bottom))' }}>
        <AnimatePresence mode="wait">
          {mode === 'evolve' && selectedForEvolve.length > 0 && !evolveSource && (
            <motion.button initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} onClick={async () => {
              if (selectedForEvolve.length !== 3) return;
              const firstPrism = prisms.find(p => p.id === selectedForEvolve[0]);
              const baseStars = firstPrism ? firstPrism.stars : 1;
              const cost = baseStars === 1 ? 2500 : (baseStars === 2 ? 10000 : 15000);
              if (user.coins < cost) {
                audioEngine.playNotEnough();
                onShowAlert?.(t('altar.alerts.not_enough_coins'), t('altar.alerts.not_enough_coins_msg', { cost }), "error");
                return;
              }
              withProcessingOverlay('evolving', async () => {
                setIsProcessing(true);
                try {
                  const result = await onEvolve(selectedForEvolve);
                  if (result) setSelectedForEvolve([]);
                } finally { setIsProcessing(false); }
              });
            }} disabled={selectedForEvolve.length !== 3 || isProcessing || (prisms.find(p => p.id === selectedForEvolve[0])?.stars ?? 1) >= 3}
              className={cn("w-full py-4 rounded-2xl font-num text-xl uppercase toy-3d-button border-none mb-4",
                selectedForEvolve.length === 3 ? "bg-cyan-400 text-white" : "bg-slate-200 text-slate-400")}>
              {isProcessing ? t('altar.evolve.evolving') : (selectedForEvolve.length === 3 ? (() => {
                const firstPrism = prisms.find(p => p.id === selectedForEvolve[0]);
                const baseStars = firstPrism ? firstPrism.stars : 1;
                if (baseStars >= 3) return t('altar.evolve.max_tier');
                const cost = baseStars === 1 ? 2500 : (baseStars === 2 ? 10000 : 15000);
                return t('altar.evolve.evolve_now', { cost });
              })() : t('altar.evolve.select_more', { count: 3 - selectedForEvolve.length }))}
            </motion.button>
          )}
          {mode === 'reroll' && rerollTarget && isValidFodderSelection() && !pendingReroll && (
            <motion.button initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              onClick={() => setShowRerollConfirm(true)}
              disabled={isProcessing} className="w-full py-4 bg-amber-400 text-slate-900 rounded-2xl font-num text-xl uppercase toy-3d-button border-none mb-4">
              {isProcessing ? t('altar.reroll.rerolling') : t('altar.reroll.reroll_now')}
            </motion.button>
          )}
        </AnimatePresence>
        {(!isProcessing && mode === 'inventory' && prisms.find(p => equippedIds.includes(p.id))) && (
          <div className="animate-in fade-in slide-in-from-bottom-1">
            <div className="flex justify-between items-center mb-3">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('altar.equipped_stats')}</span>
              <span className="text-[10px] font-black text-cyan-600 uppercase tracking-widest">{t('altar.active_on_load')}</span>
            </div>

            {(() => {
              const eq = prisms.find(p => equippedIds.includes(p.id))!;
              const isCorrupted = !!eq.blackStats;

              return (
                <div className="grid grid-cols-2 gap-2">
                  {/* Row 1, Col 1: Core Trait */}
                  <div className="bg-white p-2.5 rounded-2xl border border-slate-100 flex items-center gap-2 shadow-sm border-b-2 border-slate-200">
                    <div className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center shadow-sm shrink-0",
                      isCorrupted ? "bg-slate-900" : ""
                    )} style={{ backgroundColor: isCorrupted ? undefined : traitColorMap[eq.color] }}>
                      <Star className={cn("w-5 h-5 fill-current", isCorrupted ? "" : "text-white")} style={{ color: isCorrupted ? traitColorMap[eq.color] : undefined }} />
                    </div>
                    <div className="flex-1 min-w-0 flex justify-between items-center gap-1">
                      {(() => {
                        const desc = getPrismDescription(eq);
                        const [label, value] = desc.split(' +');
                        return (
                          <>
                            <div className="text-[10px] font-black text-slate-900 leading-tight truncate">{label}</div>
                            <div className="text-[10px] font-black text-slate-900 shrink-0">+{value}</div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Row 1, Col 2: Potential 1, Row 2, Col 1: Potential 2, Row 2, Col 2: Potential 3 */}
                  {[eq.slot1, eq.slot2, eq.slot3].filter(Boolean).map((pot, i) => (
                    <div key={i} className="bg-white p-2.5 rounded-2xl border border-slate-100 flex items-center gap-2 shadow-sm border-b-2 border-slate-200">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm shrink-0" style={{ backgroundColor: potentialColorMap[pot.type] }} />
                      <div className="flex-1 min-w-0 flex justify-between items-center gap-1">
                        {(() => {
                          const desc = getPotentialDescription(pot);
                          const [label, value] = desc.split(' +');
                          return (
                            <>
                              <div className="text-[10px] font-black text-slate-900 leading-tight truncate">{label}</div>
                              <div className="text-[10px] font-black text-slate-900 shrink-0">+{value}</div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>
      {/* Processing Overlay — shows for 1.5s before result */}
      <AnimatePresence>
        {processingAction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-[3000] flex flex-col items-center justify-center bg-slate-900/95 backdrop-blur-md"
          >
            <motion.div
              animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 0.8, repeat: Infinity }}
              className="mb-6"
            >
              {processingAction === 'evolving' ? (
                <div className="w-20 h-20 rounded-3xl bg-cyan-400/20 border-2 border-cyan-400/40 flex items-center justify-center">
                  <Zap className="w-10 h-10 text-cyan-400 fill-current" />
                </div>
              ) : (
                <div className="w-20 h-20 rounded-3xl bg-amber-400/20 border-2 border-amber-400/40 flex items-center justify-center">
                  <RefreshCw className="w-10 h-10 text-amber-400" />
                </div>
              )}
            </motion.div>
            <motion.p
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 0.8, repeat: Infinity }}
              className={cn(
                "text-2xl font-num font-black uppercase tracking-widest",
                processingAction === 'evolving' ? "text-cyan-400" : "text-amber-400"
              )}
            >
              {processingAction === 'evolving' ? t('altar.processing.evolving') : t('altar.processing.rerolling')}
            </motion.p>
            <div className="flex gap-1.5 mt-4">
              {[0, 1, 2].map(i => (
                <motion.div
                  key={i}
                  animate={{ opacity: [0.2, 1, 0.2] }}
                  transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.2 }}
                  className={cn("w-2 h-2 rounded-full", processingAction === 'evolving' ? "bg-cyan-400" : "bg-amber-400")}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Evolve Confirmation Pop-up */}
      <AnimatePresence>
        {bulkEvolveParams && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-[1000] flex items-center justify-center px-4"
          >
            <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-md" onClick={() => setBulkEvolveParams(null)} />
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="relative w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl flex flex-col items-center"
            >
              {(() => {
                const currentStep = bulkEvolveParams.steps[bulkEvolveParams.sliderValue - 1];
                const isAffordable = user.coins >= currentStep.totalCost;
                return (
                  <>
                    <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center mb-4">
                      <Zap className="w-8 h-8 text-cyan-500 fill-cyan-500" />
                    </div>
                    <h4 className="text-2xl font-num uppercase tracking-tight text-slate-900 mb-2">{t('altar.bulk_evolve.title')}</h4>
                    <p className="text-sm font-num text-slate-500 mb-6 text-center">
                      {t('altar.bulk_evolve.desc', { consumed: currentStep.totalConsumed, color: bulkEvolveParams.color.toUpperCase(), count: currentStep.totalEvolved })}
                    </p>
                    
                    {/* SLIDER */}
                    {bulkEvolveParams.steps.length > 1 && (
                      <div className="w-full mb-6 px-2">
                        <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                          <span>{t('altar.bulk_evolve.slider_min')}</span>
                          <span>{t('altar.bulk_evolve.slider_max', { max: bulkEvolveParams.steps.length })}</span>
                        </div>
                        <input 
                          type="range" 
                          min="1" 
                          max={bulkEvolveParams.steps.length} 
                          value={bulkEvolveParams.sliderValue} 
                          onChange={(e) => setBulkEvolveParams({...bulkEvolveParams, sliderValue: parseInt(e.target.value)})}
                          className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                        />
                      </div>
                    )}

                    <div className={cn("w-full rounded-2xl p-4 mb-6 border flex flex-col items-center justify-center gap-1", isAffordable ? "bg-slate-50 border-slate-100" : "bg-rose-50 border-rose-200")}>
                      <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{t('altar.bulk_evolve.total_cost')}</span>
                      <div className={cn("flex items-center gap-1.5", isAffordable ? "text-amber-500" : "text-rose-500")}>
                        <CurrencyIcon type="coin" className="w-4 h-4" />
                        <span className="text-lg font-num font-black">{currentStep.totalCost.toLocaleString()}</span>
                      </div>
                      {!isAffordable && (
                        <div className="text-[10px] font-bold text-rose-500 mt-1 uppercase">{t('altar.bulk_evolve.not_enough_coins')}</div>
                      )}
                    </div>

                    <div className="flex gap-3 w-full">
                      <button onClick={() => setBulkEvolveParams(null)} className="flex-1 py-4 rounded-2xl font-num text-sm uppercase text-slate-400 bg-slate-100 hover:bg-slate-200 transition-all active:scale-95">{t('altar.bulk_evolve.cancel')}</button>
                      <button 
                        onClick={executeBulkEvolve} 
                        disabled={!isAffordable}
                        className={cn(
                          "flex-1 py-4 rounded-2xl font-num text-sm uppercase text-white shadow-lg transition-all active:scale-95",
                          isAffordable ? "bg-cyan-500 hover:bg-cyan-600 shadow-cyan-500/30" : "bg-slate-300 shadow-none cursor-not-allowed"
                        )}
                      >
                        {t('altar.bulk_evolve.confirm')}
                      </button>
                    </div>
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reroll Confirmation Pop-up */}

      <AnimatePresence>
        {showRerollConfirm && rerollTarget && rerollFodders.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-[2000] flex items-center justify-center p-6">
            <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-md" onClick={() => setShowRerollConfirm(false)} />
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} className="bg-white rounded-[40px] w-full max-w-sm p-8 relative z-10 shadow-2xl overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 to-rose-400" />

              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mb-6">
                  <Dice5 className="w-8 h-8 text-amber-500" />
                </div>

                <h4 className="text-2xl font-num uppercase tracking-tight text-slate-900 mb-2">{t('altar.reroll.confirm_title')}</h4>
                <p className="text-sm text-slate-500 font-medium leading-relaxed mb-6">
                  {t('altar.reroll.confirm_desc', { count: rerollFodders.length, plural: rerollFodders.length > 1 ? 's' : '' })}
                </p>

                <div className="w-full bg-slate-50 rounded-3xl p-6 mb-8 text-left border border-slate-100">
                  {(() => {
                    const target = prisms.find(p => p.id === rerollTarget)!;
                    const fodders = prisms.filter(p => rerollFodders.includes(p.id));
                    const isReroll1 = fodders.length === 1 && fodders[0].color === target.color;

                    return (
                      <>
                        <div className="flex items-center gap-3 mb-4">
                          <div className={cn("px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-white shadow-sm", isReroll1 ? "bg-emerald-500" : "bg-amber-500")}>
                            {isReroll1 ? t('altar.reroll.stable_reroll') : t('altar.reroll.unstable_reroll')}
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="flex gap-3">
                            <div className="w-2 h-2 rounded-full bg-slate-300 mt-1.5 shrink-0" />
                            <p className="text-[11px] text-slate-600 font-bold leading-tight uppercase">
                              {t('altar.reroll.all_slots_randomized')}
                            </p>
                          </div>
                          <div className="flex gap-3">
                            <div className="w-2 h-2 rounded-full bg-slate-300 mt-1.5 shrink-0" />
                            <p className="text-[11px] text-slate-600 font-bold leading-tight uppercase">
                              {t('altar.reroll.choose_old_or_new')}
                            </p>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="grid grid-cols-2 gap-3 w-full">
                  <button onClick={() => setShowRerollConfirm(false)} className="py-4 rounded-2xl font-num text-sm uppercase text-slate-400 bg-slate-100 hover:bg-slate-200 transition-all active:scale-95">{t('altar.reroll.cancel')}</button>
                  <button
                    onClick={async () => {
                      setShowRerollConfirm(false);
                      setIsFodderModalOpen(false);
                      withProcessingOverlay('rerolling', async () => {
                        setIsProcessing(true);
                        try {
                          const result = await onReroll(rerollTarget, rerollFodders);
                          if (result) {
                            setPendingReroll({ ...result, fodderIds: rerollFodders });
                          }
                        } finally { setIsProcessing(false); }
                      });
                    }}
                    className="py-4 rounded-2xl font-num text-sm uppercase text-white bg-slate-900 shadow-lg shadow-slate-200 hover:shadow-xl transition-all active:scale-95"
                  >
                    {t('altar.reroll.confirm')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
